import { inject, Injectable } from '@angular/core';
import { ContentModel } from '@libs/cms/models';
import {
  FormattedPrice,
  ProductBundleType,
  ProductPriceType} from '@libs/shop/models';
import { GTMPageBlockTrackingModel } from '@libs/shared/models';
import { ProductTrackingModel, TrackingCatalogSearchSuggestionModel, TrackingPartialProductModel, TrackingProduct, TrackingProductModel } from '@libs/shop/models';
import { PricesFormatter } from '@libs/shared/services';
import { TrackingDataFields } from '../context';
import { formatPageBlockTrackingContentType, getCreativeSlot } from './helper';

@Injectable({ providedIn: 'root' })
export class TrackingMapperService {
  private readonly priceFormatter: PricesFormatter = inject(PricesFormatter);

  private readonly bundleTypeMap: Record<ProductBundleType, string> = {
    closed: 'CLOSED_PROMOTION',
    open: 'OPEN_PROMOTION',
    advantage: 'ADVANTAGE',
    reimbursement: 'REIMBURSEMENT',
  };

  private readonly priceTypeMap: Record<ProductPriceType, string> = {
    default: '',
    'price-best': 'BEST_PRICE',
    'price-promotion': 'PROMOTION',
    'price-discount': 'SAVINGS',
    'price-advantage': 'ADVANTAGE',
    'price-advantage-free': 'CLOSED_PROMOTION',
    'price-reimbursement': 'REIMBURSEMENT',
  };

  public mapTrackingProduct(item: TrackingProduct, trackingList: string): ProductTrackingModel[] {
    return item.type === 'partialProduct'
      ? this.mapPartialProductToProductTracking([item], trackingList)
      : this.mapProductToProductTracking([item], trackingList);
  }

  public mapProductToProductTracking(items: (TrackingProductModel | TrackingCatalogSearchSuggestionModel)[], trackingList: string): ProductTrackingModel[] {
    const trackingProducts: ProductTrackingModel[] = [];

    for (const item of items) {
      const productItem: TrackingProductModel = (item as TrackingProductModel);
      const searchItem: TrackingCatalogSearchSuggestionModel = (item as TrackingCatalogSearchSuggestionModel);
      const bundleName: string = productItem.details ? productItem.details.name : searchItem.name;

      const position: number = item.position + 1;

      if (item.bundleType) {
        trackingProducts.push({
          id: item.sku,
          price: this.formatSalesUnitPrice(item.prices?.bundlePrice),
          quantity: item.quantity,
          list: productItem.trackingList ?? trackingList,
          position: position,
          bundleID: item.sku,
          bundleName: bundleName,
          promotionType: this.mapPromotionType(item.bundleType, item.prices?.priceType),
          originalPrice: this.mapOriginalPrice(item.prices?.salesUnitStrikethroughPrice),
          availability: productItem.availability,
        });

        if (item.bundle) {
          for (const bundledItem of item.bundle.products) {
            trackingProducts.push({
              id: bundledItem.sku,
              price: this.formatSalesUnitPrice(bundledItem.prices?.salesUnit),
              quantity: bundledItem.quantity,
              list: productItem.trackingList ?? trackingList,
              position: position,
              bundleID: item.sku,
              bundleName: bundleName,
              promotionType: this.mapPromotionType(item.bundleType, bundledItem.prices?.priceType),
              originalPrice: this.mapOriginalPrice(bundledItem.prices?.salesUnitStrikethroughPrice),
            });
          }
        }
      } else {
        trackingProducts.push({
          id: item.sku,
          price: this.formatSalesUnitPrice(item.prices?.salesUnit),
          quantity: item.quantity,
          list: productItem.trackingList ?? trackingList,
          position: position,
          bundleID: '',
          bundleName: '',
          promotionType: this.mapPromotionType(undefined, item.prices?.priceType),
          originalPrice: this.mapOriginalPrice(item.prices?.salesUnitStrikethroughPrice),
          availability: productItem.availability,
        });
      }
    }

    return trackingProducts;
  }

  public mapPartialProductToProductTracking(items: TrackingPartialProductModel[], trackingList: string): ProductTrackingModel[] {
    const trackingProducts: ProductTrackingModel[] = [];

    for (const item of items) {
      const position: number = item.position + 1;

      trackingProducts.push({
        id: item.sku,
        price: this.formatSalesUnitPrice(item.salesUnitPrice),
        quantity: item.quantity,
        list: item.trackingList ?? trackingList,
        position: position,
        bundleID: '',
        bundleName: '',
        promotionType: '',
        originalPrice: this.mapOriginalPrice(undefined),
        availability: item.availability,
      });
    }

    return trackingProducts;
  }

  public mapPageBlockToPageBlockTracking(context: ContentModel, data: TrackingDataFields | undefined): GTMPageBlockTrackingModel {
    const blockId: string | undefined = context.id?.toString();
    const blockItemId: string | undefined = data?.id?.toString();
    const creativeSlot: string = getCreativeSlot(context, blockItemId);
    const contentType: string = formatPageBlockTrackingContentType(data?.type ?? context.type ?? '');

    return {
      promotion_id: blockItemId ? `${blockId}-${blockItemId}` : blockId,
      promotion_name: context.name ?? '',
      creative_name: data?.name ?? '',
      creative_slot: creativeSlot,
      personalization_segment: data?.personalizationSegment ?? 'no_personalization',
      content_type: contentType,
    };
  }

  public mapPromotionType(bundleType: ProductBundleType | undefined, priceType: ProductPriceType | undefined): string {
    if (bundleType && this.bundleTypeMap[bundleType]) {
      return this.bundleTypeMap[bundleType];
    }

    return priceType ? this.priceTypeMap[priceType] : '';
  }

  public mapOriginalPrice(strikethroughPrice: number | undefined): number | '' {
    return strikethroughPrice !== undefined ? Number(this.formatPrice(strikethroughPrice)) : '';
  }

  public formatSalesUnitPrice(salesUnit: number | undefined): string {
    if (salesUnit === undefined) {
      return '0';
    }

    return this.formatPrice(salesUnit);
  }

  public formatPrice(price: number): string {
    const formattedPrice: FormattedPrice = this.priceFormatter.formatPrice(price);
    const priceWithoutThousandsDot: string = formattedPrice.priceRoundedToHundredth.replaceAll('.', '');

    return priceWithoutThousandsDot.replace(',', '.');
  }
}
